import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { ChartData, IndicatorValue, AlertPayload } from '../types';
import { SCANNER_SIGNALR_URL, SIGNALR_URL, TRADING_SIGNALR_URL } from '../env';
import { ACCESS_TOKEN_KEY } from '../auth/apiClient';

// The SignalR hubs are [Authorize]. A browser WebSocket can't send an
// Authorization header, so the JWT rides the `access_token` query param.
// accessTokenFactory is re-invoked on every (re)connect, so it always picks
// up the current token — one rotated by apiClient's refresh, or one that only
// appears after login (an early tokenless attempt just fails and SignalR's
// automatic reconnect retries once the user is authenticated).
const signalrConnectionOptions = {
  accessTokenFactory: () => localStorage.getItem(ACCESS_TOKEN_KEY) ?? '',
};

// Backend BarState interface to match the C# model
export interface BarState {
  timeUnits: number;
  currentTimeUnits: number;
  symbol: string;
  openBidPrice: number;
  openAskPrice: number;
  closeBidPrice: number;
  closeAskPrice: number;
  highBidPrice: number;
  highAskPrice: number;
  lowBidPrice: number;
  lowAskPrice: number;
  volume: number;
  vwap?: number;
  tradeCount?: number;
  barStartTime: string;
  barEndTime: string;
  opened: boolean;
  closed: boolean;
  grainKey: string;
  isFinal: boolean; // True when bar is closed/finalized, false when still building
  // OHLC values from backend (mid-price of bid/ask)
  open?: number;
  high?: number;
  low?: number;
  close?: number;
}

export interface SignalRCallbacks {
  onChartUpdated?: (chartId: string, chart: ChartData) => void;
  onBarUpdated?: (symbol: string, bar: BarState) => void;
  onIndicatorUpdated?: (chartId: string, indicatorName: string, values: IndicatorValue[]) => void;
  onAlertTriggered?: (alert: AlertPayload) => void;
}

class SignalRService {
  private connection: HubConnection | null = null;
  private scannerConnection: HubConnection | null = null;
  private tradingConnection: HubConnection | null = null;
  private callbacks: SignalRCallbacks = {};
  private callbackListeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private autoStartRetries = 0;
  private startPromise: Promise<void> | null = null;
  private scannerStartPromise: Promise<void> | null = null;
  private tradingStartPromise: Promise<void> | null = null;
  private isInitialized = false;

  constructor() {
    this.initializeConnection();
    // Auto-start the connection (retries until authenticated — see below).
    this.autoStartWithRetry();
  }

  private initializeConnection() {
    if (this.isInitialized) return;
    
    // Resolve from env helper (supports Vite & Bun browsers)
    const hubUrl = SIGNALR_URL;

    console.log('Initializing SignalR connection to:', hubUrl);
    
    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, signalrConnectionOptions)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    console.log('Initializing scanner SignalR connection to:', SCANNER_SIGNALR_URL);

    this.scannerConnection = new HubConnectionBuilder()
      .withUrl(SCANNER_SIGNALR_URL, signalrConnectionOptions)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    this.tradingConnection = new HubConnectionBuilder()
      .withUrl(TRADING_SIGNALR_URL, signalrConnectionOptions)
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    this.setupEventHandlers();
    this.setupScannerEventHandlers();
    this.setupTradingEventHandlers();
    this.isInitialized = true;
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // Handle incoming messages
    this.connection.on('ChartUpdated', (chartId: string, chart: ChartData) => {
      console.log('Chart updated:', chartId, chart);
      this.callbacks.onChartUpdated?.(chartId, chart);
    });

    this.connection.on('BarUpdated', (symbol: string, bar: BarState) => {
      // Commented out to reduce console noise - uncomment for debugging
      // console.log('Bar updated:', symbol, bar);
      // Call the single callback if set
      this.callbacks.onBarUpdated?.(symbol, bar);
      // Also call all registered listeners
      const listeners = this.callbackListeners.get('barUpdate');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(symbol, bar);
          } catch (error) {
            console.error('Error in bar update listener:', error);
          }
        });
      }
    });

    // Register empty handler to prevent console warnings about missing method
    // Backend still sends tick events but UI doesn't use them
    this.connection.on('TickReceived', () => {
      // Intentionally empty - ticks not used in UI
    });

    // Handle indicator updates from backend
    this.connection.on('IndicatorUpdated', (chartId: string, indicatorName: string, values: IndicatorValue[]) => {
      this.callbacks.onIndicatorUpdated?.(chartId, indicatorName, values);
      const listeners = this.callbackListeners.get('indicatorUpdate');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(chartId, indicatorName, values);
          } catch (error) {
            console.error('Error in indicator update listener:', error);
          }
        });
      }
    });

    // Handle market indices updates (server pushes every 10s via ChartHub)
    this.connection.on('MarketIndicesUpdated', (indices: any) => {
      const listeners = this.callbackListeners.get('marketIndices');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(indices);
          } catch (error) {
            console.error('Error in market indices listener:', error);
          }
        });
      }
    });

    // Handle signal alerts from backend
    this.connection.on('AlertTriggered', (alert: AlertPayload) => {
      this.callbacks.onAlertTriggered?.(alert);
      const listeners = this.callbackListeners.get('alertTriggered');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(alert);
          } catch (error) {
            console.error('Error in alert listener:', error);
          }
        });
      }
    });

    // Handle connection events
    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
      this.reconnectAttempts++;
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected with connection ID:', connectionId);
      this.reconnectAttempts = 0;
      this.resubscribeToGroups();
    });

    this.connection.onclose((error) => {
      console.log('SignalR connection closed:', error);
      if (error) {
        console.error('Connection closed with error:', error);
      }
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnection();
      }
    });
  }

  private async attemptReconnection() {
    if (!this.connection) return;

    try {
      await this.connection.start();
      console.log('SignalR reconnected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('SignalR reconnection failed:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => this.attemptReconnection(), 5000);
      }
    }
  }

  private subscribedSymbols: Set<string> = new Set();
  private subscribedCharts: Set<string> = new Set();
  private subscribedScanners: Set<string> = new Set();
  private backtestScannerGroups: Set<string> = new Set();
  private backtestClockGroups: Set<string> = new Set();
  private backtestMarketIndicesGroups: Set<string> = new Set();

  private setupScannerEventHandlers() {
    if (!this.scannerConnection) return;

    this.scannerConnection.on('ScannerUpdated', (scannerId: string, update: any) => {
      const listeners = this.callbackListeners.get('scannerUpdate');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(scannerId, update);
          } catch (error) {
            console.error('Error in scanner update listener:', error);
          }
        });
      }
    });

    this.scannerConnection.on('ScannerMetadata', (scannerId: string, metadata: any) => {
      const listeners = this.callbackListeners.get('scannerMetadata');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(scannerId, metadata);
          } catch (error) {
            console.error('Error in scanner metadata listener:', error);
          }
        });
      }
    });

    this.scannerConnection.on('BacktestClockTick', (tick: any) => {
      const listeners = this.callbackListeners.get('backtestClock');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(tick);
          } catch (error) {
            console.error('Error in backtest clock listener:', error);
          }
        });
      }
    });

    this.scannerConnection.on('BacktestMarketIndicesUpdated', (indices: any) => {
      const listeners = this.callbackListeners.get('backtestMarketIndices');
      if (listeners) {
        listeners.forEach(listener => {
          try {
            (listener as any)(indices);
          } catch (error) {
            console.error('Error in backtest market indices listener:', error);
          }
        });
      }
    });

    this.scannerConnection.onreconnected(() => {
      this.resubscribeToScannerGroups();
    });
  }

  private async resubscribeToGroups() {
    if (!this.connection) return;

    try {
      // Resubscribe to symbols
      for (const symbol of this.subscribedSymbols) {
        await this.connection.invoke('SubscribeToSymbol', symbol);
      }

      // Resubscribe to charts
      for (const chartId of this.subscribedCharts) {
        await this.connection.invoke('JoinChartGroup', chartId);
      }

      await this.resubscribeToScannerGroups();
    } catch (error) {
      console.error('Failed to resubscribe to groups:', error);
    }
  }

  private async resubscribeToScannerGroups() {
    if (!this.scannerConnection || this.scannerConnection.state !== 'Connected') return;

    for (const scannerId of this.subscribedScanners) {
      await this.scannerConnection.invoke('SubscribeToScanner', scannerId);
    }

    for (const key of this.backtestScannerGroups) {
      const [sessionId, scannerId] = key.split('|');
      await this.scannerConnection.invoke('JoinBacktestScannerGroup', sessionId, scannerId);
    }

    for (const sessionId of this.backtestClockGroups) {
      await this.scannerConnection.invoke('JoinBacktestClockGroup', sessionId);
    }

    for (const sessionId of this.backtestMarketIndicesGroups) {
      await this.scannerConnection.invoke('JoinBacktestMarketIndicesGroup', sessionId);
    }
  }

  // Ensure connection is started (thread-safe)
  async ensureStarted(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    if (this.connection?.state === 'Connected') {
      return Promise.resolve();
    }

    this.startPromise = this.start();

    try {
      await this.startPromise;
    } finally {
      this.startPromise = null;
    }
  }

  // The chart hub is [Authorize]: an auto-start fired before the user has
  // logged in 401s. SignalR's automatic-reconnect only revives a connection
  // that was established then dropped — NOT one whose initial start failed —
  // so the connection would otherwise stay dead after login. Retry with
  // backoff; accessTokenFactory supplies the JWT once it exists, so a retry
  // shortly after login succeeds.
  private autoStartWithRetry(): void {
    this.ensureStarted()
      .then(() => {
        this.autoStartRetries = 0;
        this.reconnectAttempts = 0;
      })
      .catch(() => {
        this.autoStartRetries++;
        const delay = Math.min(3000 * this.autoStartRetries, 15000);
        setTimeout(() => this.autoStartWithRetry(), delay);
      });
  }

  async ensureScannerStarted(): Promise<void> {
    if (this.scannerStartPromise) {
      return this.scannerStartPromise;
    }

    if (this.scannerConnection?.state === 'Connected') {
      return Promise.resolve();
    }

    this.scannerStartPromise = this.startScannerConnection();

    try {
      await this.scannerStartPromise;
    } finally {
      this.scannerStartPromise = null;
    }
  }

  private async startScannerConnection(): Promise<void> {
    if (!this.scannerConnection) {
      console.error('Scanner SignalR connection not initialized');
      return;
    }

    if (this.scannerConnection.state === 'Connected') return;

    try {
      await this.scannerConnection.start();
      console.log('Scanner SignalR connected successfully to:', this.scannerConnection.baseUrl);
      await this.resubscribeToScannerGroups();
    } catch (error) {
      console.error('Scanner SignalR connection failed:', error);
      throw error;
    }
  }

  async start(): Promise<void> {
    if (!this.connection) {
      console.error('SignalR connection not initialized');
      return;
    }

    if (this.connection.state === 'Connected') {
      console.log('SignalR connection already established');
      return;
    }

    if (this.connection.state === 'Connecting') {
      console.log('SignalR connection already in progress, waiting...');
      // Wait for connection to establish or fail
      return new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (this.connection!.state === 'Connected') {
            resolve();
          } else if (this.connection!.state === 'Disconnected') {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    try {
      console.log('Starting SignalR connection...');
      await this.connection.start();
      console.log('SignalR connected successfully to:', this.connection.baseUrl);
    } catch (error) {
      console.error('SignalR connection failed:', error);
      console.error('Connection state:', this.connection.state);
      console.error('Base URL:', this.connection.baseUrl);
      
      // Try to reinitialize and retry once
      if (this.reconnectAttempts === 0) {
        console.log('Attempting to reinitialize connection...');
        this.isInitialized = false;
        this.initializeConnection();
        this.reconnectAttempts++;
        return this.start();
      }
      
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (!this.connection) return;

    try {
      // Only stop if no subscribers remain
      if (this.subscribedSymbols.size === 0 && this.subscribedCharts.size === 0) {
        await this.connection.stop();
        console.log('SignalR disconnected (no active subscriptions)');
      } else {
        console.log('SignalR connection kept alive (active subscriptions exist)');
      }
    } catch (error) {
      console.error('SignalR disconnection failed:', error);
    }
  }

  async subscribeToSymbol(symbol: string): Promise<void> {
    // Ensure connection is started first
    await this.ensureStarted();
    
    if (!this.connection || this.connection.state !== 'Connected') {
      console.warn('SignalR not connected, cannot subscribe to symbol:', symbol);
      return;
    }

    try {
      await this.connection.invoke('SubscribeToSymbol', symbol);
      this.subscribedSymbols.add(symbol);
      console.log('Subscribed to symbol:', symbol);
    } catch (error) {
      console.error('Failed to subscribe to symbol:', symbol, error);
    }
  }

  async unsubscribeFromSymbol(symbol: string): Promise<void> {
    if (!this.connection || this.connection.state !== 'Connected') {
      return;
    }

    try {
      await this.connection.invoke('UnsubscribeFromSymbol', symbol);
      this.subscribedSymbols.delete(symbol);
      console.log('Unsubscribed from symbol:', symbol);
    } catch (error) {
      console.error('Failed to unsubscribe from symbol:', symbol, error);
    }
  }

  async subscribeToChart(chartId: string): Promise<void> {
    // Ensure connection is started first
    await this.ensureStarted();
    
    if (!this.connection || this.connection.state !== 'Connected') {
      console.warn('SignalR not connected, cannot subscribe to chart:', chartId);
      return;
    }

    try {
      await this.connection.invoke('JoinChartGroup', chartId);
      this.subscribedCharts.add(chartId);
      console.log('Subscribed to chart:', chartId);
    } catch (error) {
      console.error('Failed to subscribe to chart:', chartId, error);
    }
  }

  async unsubscribeFromChart(chartId: string): Promise<void> {
    if (!this.connection || this.connection.state !== 'Connected') {
      return;
    }

    try {
      await this.connection.invoke('LeaveChartGroup', chartId);
      this.subscribedCharts.delete(chartId);
      console.log('Unsubscribed from chart:', chartId);
    } catch (error) {
      console.error('Failed to unsubscribe from chart:', chartId, error);
    }
  }

  async subscribeToScanner(scannerId: string): Promise<void> {
    await this.ensureScannerStarted();

    if (!this.scannerConnection || this.scannerConnection.state !== 'Connected') {
      console.warn('Scanner SignalR not connected, cannot subscribe to scanner:', scannerId);
      return;
    }

    try {
      await this.scannerConnection.invoke('SubscribeToScanner', scannerId);
      this.subscribedScanners.add(scannerId);
    } catch (error) {
      console.error('Failed to subscribe to scanner:', scannerId, error);
    }
  }

  async unsubscribeFromScanner(scannerId: string): Promise<void> {
    if (!this.scannerConnection || this.scannerConnection.state !== 'Connected') {
      this.subscribedScanners.delete(scannerId);
      return;
    }

    try {
      await this.scannerConnection.invoke('UnsubscribeFromScanner', scannerId);
      this.subscribedScanners.delete(scannerId);
    } catch (error) {
      console.error('Failed to unsubscribe from scanner:', scannerId, error);
    }
  }

  async joinBacktestScannerGroup(sessionId: string, scannerId: string): Promise<void> {
    await this.ensureScannerStarted();
    await this.scannerConnection?.invoke('JoinBacktestScannerGroup', sessionId, scannerId);
    this.backtestScannerGroups.add(`${sessionId}|${scannerId}`);
  }

  async leaveBacktestScannerGroup(sessionId: string, scannerId: string): Promise<void> {
    if (this.scannerConnection?.state === 'Connected') {
      await this.scannerConnection.invoke('LeaveBacktestScannerGroup', sessionId, scannerId);
    }
    this.backtestScannerGroups.delete(`${sessionId}|${scannerId}`);
  }

  async leaveAllBacktestScannerGroups(sessionId: string): Promise<void> {
    const groups = Array.from(this.backtestScannerGroups).filter(key => key.startsWith(`${sessionId}|`));
    for (const key of groups) {
      const [, scannerId] = key.split('|');
      await this.leaveBacktestScannerGroup(sessionId, scannerId);
    }
  }

  async joinBacktestClockGroup(sessionId: string): Promise<void> {
    await this.ensureScannerStarted();
    await this.scannerConnection?.invoke('JoinBacktestClockGroup', sessionId);
    this.backtestClockGroups.add(sessionId);
  }

  async leaveBacktestClockGroup(sessionId: string): Promise<void> {
    if (this.scannerConnection?.state === 'Connected') {
      await this.scannerConnection.invoke('LeaveBacktestClockGroup', sessionId);
    }
    this.backtestClockGroups.delete(sessionId);
  }

  async joinBacktestMarketIndicesGroup(sessionId: string): Promise<void> {
    await this.ensureScannerStarted();
    await this.scannerConnection?.invoke('JoinBacktestMarketIndicesGroup', sessionId);
    this.backtestMarketIndicesGroups.add(sessionId);
  }

  async leaveBacktestMarketIndicesGroup(sessionId: string): Promise<void> {
    if (this.scannerConnection?.state === 'Connected') {
      await this.scannerConnection.invoke('LeaveBacktestMarketIndicesGroup', sessionId);
    }
    this.backtestMarketIndicesGroups.delete(sessionId);
  }

  setCallbacks(callbacks: SignalRCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Add a listener for bar updates (supports multiple listeners)
  addBarUpdateListener(id: string, callback: (symbol: string, bar: BarState) => void): void {
    if (!this.callbackListeners.has('barUpdate')) {
      this.callbackListeners.set('barUpdate', new Set());
    }
    // Store with ID for later removal
    const listeners = this.callbackListeners.get('barUpdate')!;
    // Remove old listener with same ID if exists
    listeners.forEach(listener => {
      if ((listener as any).id === id) {
        listeners.delete(listener);
      }
    });
    // Add new listener with ID
    (callback as any).id = id;
    listeners.add(callback);
  }

  // Remove a specific bar update listener
  removeBarUpdateListener(id: string): void {
    const listeners = this.callbackListeners.get('barUpdate');
    if (listeners) {
      listeners.forEach(listener => {
        if ((listener as any).id === id) {
          listeners.delete(listener);
        }
      });
    }
  }

  // Add a listener for indicator updates
  addIndicatorUpdateListener(id: string, callback: (chartId: string, indicatorName: string, values: IndicatorValue[]) => void): void {
    if (!this.callbackListeners.has('indicatorUpdate')) {
      this.callbackListeners.set('indicatorUpdate', new Set());
    }
    const listeners = this.callbackListeners.get('indicatorUpdate')!;
    listeners.forEach(listener => {
      if ((listener as any).id === id) listeners.delete(listener);
    });
    (callback as any).id = id;
    listeners.add(callback);
  }

  removeIndicatorUpdateListener(id: string): void {
    const listeners = this.callbackListeners.get('indicatorUpdate');
    if (listeners) {
      listeners.forEach(listener => {
        if ((listener as any).id === id) listeners.delete(listener);
      });
    }
  }

  // Add a listener for alert events
  addAlertListener(id: string, callback: (alert: AlertPayload) => void): void {
    if (!this.callbackListeners.has('alertTriggered')) {
      this.callbackListeners.set('alertTriggered', new Set());
    }
    const listeners = this.callbackListeners.get('alertTriggered')!;
    listeners.forEach(listener => {
      if ((listener as any).id === id) listeners.delete(listener);
    });
    (callback as any).id = id;
    listeners.add(callback);
  }

  removeAlertListener(id: string): void {
    const listeners = this.callbackListeners.get('alertTriggered');
    if (listeners) {
      listeners.forEach(listener => {
        if ((listener as any).id === id) listeners.delete(listener);
      });
    }
  }

  addScannerUpdateListener(id: string, callback: (scannerId: string, update: any) => void): void {
    this.addListener('scannerUpdate', id, callback);
  }

  removeScannerUpdateListener(id: string): void {
    this.removeListener('scannerUpdate', id);
  }

  addScannerMetadataListener(id: string, callback: (scannerId: string, metadata: any) => void): void {
    this.addListener('scannerMetadata', id, callback);
  }

  removeScannerMetadataListener(id: string): void {
    this.removeListener('scannerMetadata', id);
  }

  addBacktestClockListener(id: string, callback: (tick: any) => void): void {
    this.addListener('backtestClock', id, callback);
  }

  removeBacktestClockListener(id: string): void {
    this.removeListener('backtestClock', id);
  }

  addMarketIndicesListener(id: string, callback: (indices: any) => void): void {
    this.addListener('marketIndices', id, callback);
  }

  removeMarketIndicesListener(id: string): void {
    this.removeListener('marketIndices', id);
  }

  addBacktestMarketIndicesListener(id: string, callback: (indices: any) => void): void {
    this.addListener('backtestMarketIndices', id, callback);
  }

  removeBacktestMarketIndicesListener(id: string): void {
    this.removeListener('backtestMarketIndices', id);
  }

  private addListener(eventName: string, id: string, callback: Function): void {
    if (!this.callbackListeners.has(eventName)) {
      this.callbackListeners.set(eventName, new Set());
    }
    const listeners = this.callbackListeners.get(eventName)!;
    listeners.forEach(listener => {
      if ((listener as any).id === id) listeners.delete(listener);
    });
    (callback as any).id = id;
    listeners.add(callback);
  }

  private removeListener(eventName: string, id: string): void {
    const listeners = this.callbackListeners.get(eventName);
    if (!listeners) return;
    listeners.forEach(listener => {
      if ((listener as any).id === id) listeners.delete(listener);
    });
  }

  getConnectionState(): string {
    if (!this.connection) return 'Disconnected';
    
    const state = this.connection.state;
    
    // Map SignalR states to more user-friendly names
    switch (state) {
      case 'Connected':
        return 'Connected';
      case 'Connecting':
        return 'Connecting';
      case 'Reconnecting':
        return 'Reconnecting';
      case 'Disconnected':
        return 'Disconnected';
      case 'Disconnecting':
        return 'Disconnecting';
      default:
        return state || 'Disconnected';
    }
  }

  isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  // Get subscription info for debugging
  getSubscriptionInfo(): { symbols: string[], charts: string[] } {
    return {
      symbols: Array.from(this.subscribedSymbols),
      charts: Array.from(this.subscribedCharts)
    };
  }

  // Subscribe to bar updates with callback
  subscribeToBarUpdates(callback: (barState: BarState) => void): void {
    this.setCallbacks({
      ...this.callbacks,
      onBarUpdated: (_symbol: string, bar: BarState) => {
        callback(bar);
      }
    });
  }

  // Force connection restart if needed
  async forceRestart(): Promise<void> {
    console.log('Forcing SignalR connection restart...');
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.warn('Error stopping connection during restart:', error);
      }
    }
    
    this.isInitialized = false;
    this.startPromise = null;
    this.initializeConnection();
    await this.ensureStarted();
    
    // Resubscribe to all groups
    await this.resubscribeToGroups();
  }

  // ── Trading Hub ──────────────────────────────────────────────

  private setupTradingEventHandlers() {
    if (!this.tradingConnection) return;

    this.tradingConnection.on('TradeOpened', (trade: any) => {
      this.emitTradingEvent('tradeOpened', trade);
    });

    this.tradingConnection.on('TradeClosed', (trade: any) => {
      this.emitTradingEvent('tradeClosed', trade);
    });

    this.tradingConnection.on('TradeUpdated', (trade: any) => {
      this.emitTradingEvent('tradeUpdated', trade);
    });

    this.tradingConnection.on('StopAdjusted', (data: any) => {
      this.emitTradingEvent('stopAdjusted', data);
    });

    this.tradingConnection.onreconnected(() => {
      console.log('Trading SignalR reconnected');
    });
  }

  private emitTradingEvent(eventType: string, data: any) {
    const key = `trading-${eventType}`;
    const listeners = this.callbackListeners.get(key);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          (listener as any)(data);
        } catch (error) {
          console.error(`Error in trading ${eventType} listener:`, error);
        }
      });
    }
  }

  async ensureTradingStarted(): Promise<void> {
    if (this.tradingStartPromise) return this.tradingStartPromise;
    if (this.tradingConnection?.state === 'Connected') return;

    this.tradingStartPromise = this.startTradingConnection();
    try {
      await this.tradingStartPromise;
    } finally {
      this.tradingStartPromise = null;
    }
  }

  private async startTradingConnection(): Promise<void> {
    if (!this.tradingConnection) return;
    if (this.tradingConnection.state === 'Connected') return;

    try {
      await this.tradingConnection.start();
      console.log('Trading SignalR connected to:', TRADING_SIGNALR_URL);
    } catch (error) {
      console.error('Trading SignalR connection failed:', error);
    }
  }

  async subscribeToTradingRule(ruleId: string): Promise<void> {
    await this.ensureTradingStarted();
    await this.tradingConnection?.invoke('SubscribeToRule', ruleId);
  }

  async unsubscribeFromTradingRule(ruleId: string): Promise<void> {
    await this.tradingConnection?.invoke('UnsubscribeFromRule', ruleId);
  }

  async subscribeToTradingPortfolio(): Promise<void> {
    await this.ensureTradingStarted();
    await this.tradingConnection?.invoke('SubscribeToPortfolio');
  }

  async unsubscribeFromTradingPortfolio(): Promise<void> {
    await this.tradingConnection?.invoke('UnsubscribeFromPortfolio');
  }

  async subscribeToBacktestTrades(sessionId: string): Promise<void> {
    await this.ensureTradingStarted();
    await this.tradingConnection?.invoke('SubscribeToBacktestTrades', sessionId);
  }

  async unsubscribeFromBacktestTrades(sessionId: string): Promise<void> {
    await this.tradingConnection?.invoke('UnsubscribeFromBacktestTrades', sessionId);
  }

  addTradingListener(id: string, eventType: 'tradeOpened' | 'tradeClosed' | 'tradeUpdated' | 'stopAdjusted', callback: (data: any) => void): void {
    const key = `trading-${eventType}`;
    if (!this.callbackListeners.has(key)) {
      this.callbackListeners.set(key, new Set());
    }
    const listeners = this.callbackListeners.get(key)!;
    listeners.forEach(l => { if ((l as any).id === id) listeners.delete(l); });
    (callback as any).id = id;
    listeners.add(callback);
  }

  removeTradingListener(id: string, eventType: 'tradeOpened' | 'tradeClosed' | 'tradeUpdated' | 'stopAdjusted'): void {
    const key = `trading-${eventType}`;
    const listeners = this.callbackListeners.get(key);
    if (listeners) {
      listeners.forEach(l => { if ((l as any).id === id) listeners.delete(l); });
    }
  }
}

// Export a singleton instance
export const signalRService = new SignalRService();
export default signalRService;
